import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InvestmentPlaceholder } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentPlaceholder/InvestmentPlaceholder.tsx';
import { Button, Flex, Text } from '@mantine/core';
import { CardBodySkeleton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/CardBodySkeleton.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { AcceptanceStatus, KYCFileType } from '@dealstack/shared/lib/generated';
import { useInvestmentDetailsKycStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsKycStatus.ts';
import { InvestmentDetailsRowItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/components/InvestmentDetailsRowItem/InvestmentDetailsRowItem.tsx';
import {
	getDeclarationsSummary,
	getNormalizedKycReviewStatus,
	getTranslatedKycFileType,
	getTranslationFromStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.helper.ts';
import { EXPECTED_STATUS } from '@dealstack/shared/lib/common/business/kyc';
import {
	getKycStatusColor,
	KycDisplayStatus,
	resolveStatusBadgeColors,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/statusStyling.helper.ts';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge.tsx';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string';
import { useParams } from 'react-router-dom';
import { ManageRequirementsButton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/ManageRequirementsButton.tsx';

interface Props {
	onManageRequirements: () => void;
	onReviewKyc: () => void;
	canMutate: boolean;
}

interface Row {
	key: string;
	name: string;
	status: AcceptanceStatus | KycDisplayStatus;
}

const renderStatusBadge = (
	status: AcceptanceStatus | KycDisplayStatus,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
) => {
	const { color, textColor } = resolveStatusBadgeColors(getKycStatusColor(status));
	const text =
		status === EXPECTED_STATUS
			? tMipManage('kyc_status.expected')
			: getTranslationFromStatus(status, tMipManage);

	if (!text) {
		return null;
	}

	return <StatusBadge text={text} color={color} textColor={textColor} />;
};

export const InvestmentKycStatus = ({ onManageRequirements, onReviewKyc, canMutate }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { mipId } = useParams();
	const { data, isLoading } = useInvestmentDetailsKycStatus();

	const isKycRequired = data?.isKycRequired;
	const kycList = data?.kyc ?? [];
	const expectedKycList = data?.expectedKyc ?? [];
	const declarationsList = data?.declarations ?? [];

	const declarationsSummary =
		declarationsList.length > 0 ? getDeclarationsSummary(declarationsList, tMipManage) : null;
	const kycWithoutForms = kycList.filter((item) => item.type !== KYCFileType.FORM);
	const expectedKycWithoutForms = expectedKycList.filter(
		(item) => item.type !== KYCFileType.FORM
	);

	const rows: Row[] = isKycRequired
		? [
				declarationsSummary
					? {
							key: 'declarations',
							name: declarationsSummary.text,
							status: declarationsSummary.overallStatus,
						}
					: {
							key: 'declarations',
							name: tMipManage('kyc_type.declarations'),
							status: EXPECTED_STATUS,
						},
				...kycWithoutForms.map<Row>((item) => ({
					key: item.id,
					name: getTranslatedKycFileType(item.type, tMipManage),
					status: getNormalizedKycReviewStatus(item.status),
				})),
				...expectedKycWithoutForms
					.filter((item) => !item.isSubmitted)
					.map<Row>((item) => ({
						key: `expected-${item.type}`,
						name: getTranslatedKycFileType(item.type, tMipManage),
						status: EXPECTED_STATUS,
					})),
			]
		: [];

	const renderBody = () => {
		if (isLoading) {
			return <CardBodySkeleton count={4} mih={151} />;
		}

		if (rows.length > 0) {
			return (
				<Flex direction="column" p="0.5rem">
					{rows.map((row, index) => (
						<InvestmentDetailsRowItem
							key={row.key}
							label={<Text c="dark.0">{row.name}</Text>}
							content={renderStatusBadge(row.status, tMipManage)}
							hideBorder={index === 0}
						/>
					))}
				</Flex>
			);
		}

		return (
			<InvestmentPlaceholder
				icon="certificate-awarded"
				label={tMipManage('investment_kyc_status.placeholder_label')}
				mih={151}
			/>
		);
	};

	return (
		<InvestmentDetailsCard
			cardLabel={tMipManage('investment_kyc_status.card_label')}
			actions={
				<>
					<ManageRequirementsButton canMutate={canMutate} onClick={onManageRequirements}>
						{tMipManage('investment_kyc_status.button.manage_requirements')}
					</ManageRequirementsButton>

					<PermissionWrapper
						resourceType={ResourceType.PROGRAMME}
						resourceId={defaultToEmptyString(mipId)}
						permissionRole={PermissionRole.KYC_ADMIN}
					>
						<Button
							variant="default"
							rightSection={<Icon variant="arrow-up-right-2" size="sm" />}
							onClick={onReviewKyc}
						>
							{tMipManage('investment_kyc_status.button.review_kyc')}
						</Button>
					</PermissionWrapper>
				</>
			}
		>
			{renderBody()}
		</InvestmentDetailsCard>
	);
};
